import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Text,
  Avatar,
  Box,
  Divider,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  Input,
  InputRightElement,
  TabIndicator,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow, AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure, FormControl, Tooltip,
  PopoverCloseButton, Alert, AlertIcon, AlertTitle, AlertDescription, useToast, PopoverFooter, Accordion, AccordionButton, AccordionItem, AccordionIcon, AccordionPanel, Image, Flex, useBreakpointValue
} from '@chakra-ui/react';
import { FaUserTie, FaUsers } from "react-icons/fa";
import { MdVerifiedUser, MdFeedback, MdLogout } from "react-icons/md";
import { PiFilesFill } from "react-icons/pi";
import { FiEye, FiEyeOff, FiEdit3, FiCheck, FiX } from "react-icons/fi";
import { Auth } from 'aws-amplify';
import { useUserQuery, useUpcomingAppointmentsQuery, useCancelConsultMutation, useRemoveFamilyMemberFromUserMutation, useDeleteUserMutation } from '../graphql/types-and-hooks';
import { Loading } from './Loading';
import { DateTime } from 'luxon';
import AmplifyService from "../services/amplify";
import EditProfileModal from './EditProfileModal';
import { logError } from '../components/logger';
import { useUpcomingAppointmentStore } from '../store';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { RiDeleteBin2Fill } from "react-icons/ri";
import { FaHandHoldingMedical } from "react-icons/fa";
import { getCachedConfig } from '../utils/configManager';


const UserDetails = ({ isOpen, onClose, onOpenCreateFamilyMember, isAuthenticated, hasClassBAuth }) => {
  const [show, setShow] = useState(false);
  // eslint-disable-next-line
  const [changePasswordError, setChangePasswordError] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  // eslint-disable-next-line
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [email, setEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");


  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const validatePasswordCriteria = (password) => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const customerLoginService = new AmplifyService();
  const toast = useToast()

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      localStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      logError('Error signing out', error);
    }
  };

  const handleImageClose = () => {
    setSelectedImage(null);
    onImageClose();
  };

  const [config, setConfig] = useState<any>(null);
  const colors = config?.colorScheme || {};

  useEffect(() => {
    const config = getCachedConfig();
    if (config) {
      setConfig(config);
    }
  }, []);

  // eslint-disable-next-line 
  const [appointment] = useUpcomingAppointmentStore(state => [
    state.appointment,
  ]);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      window.parent.postMessage({ action: 'scrollToTop' }, '*');
    }
  }, [isOpen, isAuthenticated]);

  

  // eslint-disable-next-line 
  const [cancelConsultMutation, { loading: cancelConsultLoading }] =
    useCancelConsultMutation({
      refetchQueries: [
        'User',
        'UpcomingAppointments',
        'AppointmentSlots',
        'Calendar',
      ],
      awaitRefetchQueries: true,
    });

  const { data: userData, loading: userLoading, error: userError, refetch } = useUserQuery({
    variables: {},
  });

  // eslint-disable-next-line
  const handleImageClick = (image: string) => {
    setSelectedImage(image);
    onImageOpen();
  };

  // eslint-disable-next-line
  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const {
    data: upcomingAppointmentsData,
    loading: upcomingAppointmentsLoading,
    error: upcomingAppointmentsError,
  } = useUpcomingAppointmentsQuery();

  const [
    removeFamilyMemberFromUserMutation,
    { loading: removeFamilyMemberFromUserLoading },
  ] = useRemoveFamilyMemberFromUserMutation();

  const handleRemoveFamilyMember = async (familyMemberKey) => {
    try {
      await removeFamilyMemberFromUserMutation({
        variables: {
          input: {
            family_member_key: parseInt(familyMemberKey),
          },
        },

      });

      await refetch();

      toast({
        title: 'Family member removed successfully.',
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    } catch (error) {
      logError('Error removing family member', error);
      toast({
        title: 'Error removing family member.',
        description: "There was an issue removing the family member. Please try again.",
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const [deleteUserMutation, { loading: deleteUserLoading }] =
    useDeleteUserMutation();

  const handleDeleteAccount = async () => {
    try {
      await deleteUserMutation();
      await handleLogout();
    } catch (error) {
      logError('Error deleting account', error);
      toast({
        title: 'Error deleting account.',
        description: "There was an issue deleting your account. Please try again.",
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const doPasswordReset = () => {
    customerLoginService.forgotPassword(email).then((result: any) => {
      setResetMessage(`Password reset email has been sent`);
      // GTAG EVENT
      (window as any).gtag('event', 'auth', {
        event: 'class_b_set_password_initiated'
      });
    }).catch((e) => {
    })
  }

  const handleCancelAppointment = async (appointment) => {
    try {
      // If we cannot determine the cancellation period we prompt the user to contact the clinic
      if (!appointment?.cancellation_period && !appointment?.cancellation_period == null) {
        // Show alert to user
        return (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>{'alerts.cancellationPeriodError.heading'}</AlertTitle>
            <AlertDescription>{'alerts.cancellationPeriodError.description'}</AlertDescription>
          </Alert>
        );
      }

      const cancellationTime = appointment?.cancellation_period != null
        ? DateTime.now().plus({ hours: parseInt(appointment.cancellation_period) })
        : DateTime.now();

      const appointmentTime = DateTime.fromISO(`${appointment.date}T${appointment.time}:00`);

      if (cancellationTime > appointmentTime) {
        // Show alert to user
        return (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>{'alerts.cancellationPeriodError.heading'}</AlertTitle>
            <AlertDescription>{'alerts.cancellationPeriodError.description'}</AlertDescription>
          </Alert>
        );
      }

      await cancelConsultMutation({
        variables: {
          input: {
            clinic_group_crm: appointment.clinic_group_crm,
            user_fk: appointment.user_fk,
            location_id: appointment.location_id,
            appointment_id: appointment.appointment_id,
          },
        },
      });

      // GTAG EVENT
      (window as any).gtag('event', servicebookingFlowStore.isServiceBooking ? 'service_booking_cancelled' : 'booking_cancelled', {
        clinic_group_crm: appointment.clinic_group_crm,
        user_fk: appointment.user_fk,
        location_id: appointment.location_id,
      });

      toast({
        title: 'Appointment canceled successfully.',
        description: "We've canceled your appointment for you.",
        status: 'success',
        duration: 9000,
        isClosable: true,
      });



    } catch (error) {
      logError('Error cancelling appointment', error);
      // Show alert to user
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>{'alerts.cancellationPeriodError.heading'}</AlertTitle>
          <AlertDescription>{'alerts.cancellationPeriodError.description'}</AlertDescription>
        </Alert>
      );
    }

    return null; // Explicitly return null if no other paths return JSX
  };


  const formattedAppointmentsData = useMemo(() => {
    const appointments = upcomingAppointmentsData?.upcomingAppointments?.filter(
      appointment =>
        DateTime.fromISO(`${appointment.date}T${appointment.time}:00`) >
        DateTime.now()
    );

    return appointments || [];
  }, [upcomingAppointmentsData]);

  const formattedData = useMemo(() => {
    if (!userData) return [];

    return Object.keys(userData.user.family_members || {}).map((memberId, index) => {
      const member = userData.user.family_members[memberId];
      return (
        <Popover isLazy key={`memberid-${member.id || index}`}>
          <PopoverTrigger>
            <Button
              onClick={() => handleSelect(member)}
              mb={3}
              width="100%"
              boxShadow="md"
              borderColor='#B92F2F'
              variant='outline'
              borderRadius={30}
            >
              {`${member.first_name} ${member.last_name}`}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverHeader fontWeight='semibold' fontSize={'sm'}>{`${member.first_name} ${member.last_name}`}</PopoverHeader>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              <Text fontSize={'sm'}>Date of Birth : {`${member.dob}`}</Text>
              <Text fontSize={'sm'}>Gender : {`${member.gender}`}</Text>
              <Text fontSize={'sm'}>Phone Number : {`${member.phone_number}`}</Text>
            </PopoverBody>
            <PopoverFooter>
              <Button
                size={'xs'}
                fontSize={'xs'}
                onClick={() => handleRemoveFamilyMember(memberId)}
                isLoading={removeFamilyMemberFromUserLoading}
              >
                Remove Family Member
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      );
    });
    // eslint-disable-next-line
  }, [userData]);

  const handleSelect = member => {
    // onSelectFamilyMember(member);
    // onClose();
  };

  const handleClick = () => setShow(!show);

  const mapStringToValue = input => {
    switch (input) {
      case 'F':
        return 'Female';
      case 'M':
        return 'Male';
      default:
        return input;
    }
  };

  const onChangePassword = (event) => {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match.',
        status: 'error',
        duration: 5000, // Toast auto closes after 5 seconds
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (oldPassword === '') {
      toast({
        title: 'Error',
        description: 'Please enter your old password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (newPassword === '') {
      toast({
        title: 'Error',
        description: 'Please enter your new password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (newPassword === oldPassword) {
      toast({
        title: 'Error',
        description: 'New password must be different from the old password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (confirmNewPassword === '') {
      toast({
        title: 'Error',
        description: 'Please confirm your new password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    //setChangePasswordError('');

    customerLoginService.changePassword(oldPassword, newPassword).then(result => {
      if (result === 'SUCCESS') {
        toast({
          title: 'Password changed.',
          description: 'Your password has been changed successfully!',
          status: 'success',
          duration: 5000, // Toast auto closes after 5 seconds
          isClosable: true,
          position: 'top-right',
        });
      } else {
        setChangePasswordError(result);
        toast({
          title: 'Error',
          description: result,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }).catch(e => {
      setChangePasswordError(e.toString());
      toast({
        title: 'Error',
        description: e.toString(),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      logError('Error changing password', e);
    });
  };



  const handleOldPasswordChange = event => setOldPassword(event.currentTarget.value);

  const handleNewPasswordChange = (event) => {
    const newPassword = event.currentTarget.value;
    setNewPassword(newPassword);
    validatePasswordCriteria(newPassword);
  };

  const handleConfirmPasswordChange = event => setConfirmNewPassword(event.currentTarget.value);

  const renderUserDetails = () => {
    if (userLoading) return <Text>Loading...</Text>;
    if (userError) {
      console.log('Error loading user data', userError);
      return <Text>Error loading user data</Text>;
    }
    if (!userData) return <Text>No user data found</Text>;

    const user = userData.user;

    return (
      <>
        <Avatar name={user.first_name} size="xl" />
        <Text fontWeight="bold" fontSize="xl" mt="10px">
          {user.first_name} {user.last_name}
        </Text>
        <Text fontSize="sm">Personal Details</Text>
        <SimpleGrid columns={2} spacing={2} mt={5}>
          <Text fontSize="sm">First Name</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{user.first_name}</Text>
          <Text fontSize="sm">Last Name</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{user.last_name}</Text>
          <Text fontSize="sm">Date of Birth</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{user.dob}</Text>
          <Text fontSize="sm">Gender</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{mapStringToValue(user.gender)}</Text>
        </SimpleGrid>
        <Divider mt={7} />
        <Text fontSize="sm" mt={7}>Contact Details</Text>
        <SimpleGrid columns={2} spacing={3} mt={5}>
          <Text fontSize="sm">Email Address</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{user.email}</Text>
          <Text fontSize="sm">Mobile Number</Text>
          <Text fontSize="sm" fontWeight="bold" ml={-12}>{user.phone_number}</Text>
        </SimpleGrid>
        {user.email_verified !== "true" && (
          <Alert
            status="info"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            p={4}
            mt={5}

          >
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Attention!</AlertTitle>
              <AlertDescription>
                Your email address is not verified. We have sent a verification email to your email address. Please check your inbox and verify your email.
              </AlertDescription>
            </Box>

          </Alert>
        )}
      </>
    );
  };

  const renderFamilyMembers = () => {
    if (userLoading) return <Loading />;
    if (userError) {
      console.log('Family members loading error', userError);
      return <Text id="myFamilyScreen.error">Family members loading error</Text>;
    }
    if (!formattedData.length) return <Text id="myFamilyScreen.noResults">No Family Member added to your profile</Text>;

    return formattedData;
  };

  const renderAppointments = () => {
    if (upcomingAppointmentsLoading) return <Loading height={100} />;
    if (upcomingAppointmentsError) {
      console.log('Error loading upcoming appointments', upcomingAppointmentsError);
      return null;
    }
    if (!formattedAppointmentsData.length) return null;

    return formattedAppointmentsData.map(appointment => {
      const date = DateTime.fromISO(appointment.date);
      const day = date.day;
      const weekday = date.weekdayLong;
      const month = date.monthLong;
      const time = DateTime.fromISO(`${appointment.date}T${appointment.time}`).toFormat('hh:mm a');

      return (
        <Box key={`appointment-${appointment.appointment_id}`} mb={3} borderWidth={1} boxShadow="md" display="flex" flexDirection="row" borderRadius={10}>
          <Box bg="#B92F2F" width="100px" p={2} color={'white'} display="flex" flexDirection="column" alignItems="center" justifyContent="center" borderLeftRadius={10}>
            <Text fontSize="30px" mt={-2} fontWeight={'bold'}>{day}</Text>
            <Text fontSize="xs" mt={-2} fontWeight={'bold'}>{weekday}</Text>
            <Text fontSize="sm" mt={-2} fontWeight={'bold'}>{month}</Text>
            <Text fontSize="sm" fontWeight={'bold'}>{time}</Text>
          </Box>
          <Box p={3}>
            <Text fontSize="md" fontWeight={'bold'}>{appointment.doctor_full_name}</Text>
            <Text fontSize="sm">{appointment.location_display_name}</Text>
            <Text fontSize="sm">Type: {appointment.appointment_type_name}</Text>
            <Text fontSize="sm" fontWeight={'bold'}>Patient: {`${appointment.first_name} ${appointment.last_name}`}</Text>
            <Text fontSize="sm" fontWeight={'bold'}>AID: {`${appointment.appointment_id}`}</Text>
            <Button
              fontSize={'xs'}
              borderRadius={'full'}
              size={'xs'}
              mt={2}
              bg="#B92F2F"
              color={'white'}
              onClick={() => handleCancelAppointment(appointment)}
            >
              Cancel Appointment
            </Button>
          </Box>
        </Box>
      );
    });
  };

  const isMobile = useBreakpointValue({ base: true, lg: false }) || false;
  const [selectedTab, setSelectedTab] = useState<number | null>(null);

  if (!isAuthenticated) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="sm" blockScrollOnMount={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>My Account</DrawerHeader>
          <DrawerBody>
            <Text fontSize="lg" textAlign="center" color="red.500">
              Please log in to access your account details.
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  const tabs = [
    { id: 1, label: 'Personal Details', icon: <FaUserTie />, description: 'View and update your personal details.' },
    { id: 2, label: 'Change Password', icon: <MdVerifiedUser />, description: 'Update your account password securely.', isDisabled: hasClassBAuth === "B" },
    { id: 3, label: 'Family Members', icon: <FaUsers />, description: 'Manage family members and book for them.', isDisabled: hasClassBAuth === "B" },
    { id: 4, label: 'My Bookings', icon: <FaHandHoldingMedical />, description: 'View and manage your bookings.', isDisabled: hasClassBAuth === "B" },
    { id: 5, label: 'User Manual', icon: <MdFeedback />, description: 'Learn how to use the application.' },
    { id: 6, label: 'Privacy Policy', icon: <PiFilesFill />, description: 'Review how your data is handled.' }
  ];

  const userDetailsContent = renderUserDetails();


  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="sm" blockScrollOnMount={false}>
      <DrawerOverlay />
      <DrawerContent display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        <DrawerCloseButton />
        <DrawerHeader>My Account</DrawerHeader>
        <DrawerBody  >
          <Tabs isFitted variant="unstyled" >
            {isMobile ? (
              selectedTab === null ? (

                <TabList flexDirection="column" gap={2}>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      isDisabled={tab.isDisabled}
                      border="1px solid"
                      borderColor={colors.buttonBg || "#B92F2F"}
                      borderRadius="md"
                      width="350px"
                      padding="12px"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      gap={3}
                      _hover={{ bg: "#B92F2F", color: "#fff" }}
                      onClick={() => setSelectedTab(tab.id)}
                    >
                      <Flex alignItems="center" width="100%">
                        <Box color={colors.buttonBg || "#B92F2F"}>{tab.icon}</Box>
                        <Box flex="1" ml={5} textAlign="left">{tab.label}</Box>
                      </Flex>
                    </Tab>
                  ))}
                </TabList>
              ) : (

                <Box>
                  <Button
                    onClick={() => setSelectedTab(null)}
                    border="1px solid "
                    borderColor={colors.buttonBg || "#B92F2F"}
                    borderRadius="30px"
                    width="100px"
                    marginBottom={4}
                    size={'sm'}
                    _hover={{ bg: "#B92F2F", color: "#fff" }}
                  >
                    Back
                  </Button>

                </Box>
              )
            ) : (

              <TabList>
                {tabs.map((tab) => (
                  <Tab key={tab.id} isDisabled={tab.isDisabled}>
                    <Tooltip
                      label={
                        <Box fontSize="14px" color="#FFFFFF">
                          <strong>{tab.label}</strong>
                          <p>{tab.description}</p>
                        </Box>
                      }
                      hasArrow
                      bg="#1976d2"
                      placement="top"
                    >
                      <Box
                        borderRadius="full"
                        boxSize="30px"
                        bg={colors.buttonBg || "#B92F2F"}
                        aria-label={tab.label}
                        fontSize="15px"
                        color="#fff"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {tab.icon}
                      </Box>
                    </Tooltip>
                  </Tab>
                ))}
              </TabList>
            )}
            <TabIndicator mt='-1.5px' height='2px' bg={colors.buttonBg || "#B92F2F"} borderRadius='1px' display={{ base: 'none', lg: 'block' }} />
            {!(isMobile && selectedTab === null) && (
              <TabPanels>
                <TabPanel>
                  <Box alignItems="center" justifyContent="center" mb={'10px'}>
                    {userDetailsContent}
                    {hasClassBAuth === "B" && (
                      <Alert
                        status="warning"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        p={4}
                        mt={5}
                        mb={5}
                      >
                        <AlertIcon />
                        <Box flex="1">
                          <AlertTitle>Attention!</AlertTitle>
                          <AlertDescription >
                            You are currently logged in with basic authentication. To access all features of your account, please login with your email and password. If you dont have a password set, or need to set a password, please click this button to begin the reset password process.
                          </AlertDescription>
                        </Box>
                        {!showResetPasswordForm && (<Button
                          borderRadius={30}
                          mt={3}
                          size="sm"
                          bg="green"
                          color="#fff"
                          onClick={() => setShowResetPasswordForm(true)} // Toggle to show reset password form
                        >
                          Set your Password
                        </Button>)}

                        {showResetPasswordForm && (
                          <span>
                            <Text fontSize="md" mt={3} fontWeight={'bold'}>Enter the details below to reset your password.</Text>
                            <FormControl mt={2}>

                              <Input
                                type="text"
                                variant={'fill'}
                                value={email}
                                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                placeholder="Enter your email"
                                borderRadius="30px"

                              />
                            </FormControl>
                            <Text fontSize="sm" color={'green'}>{resetMessage}</Text>
                            <Button
                              borderRadius={30}
                              size="sm"
                              bg="green"
                              color="#fff"
                              mt={4}
                              onClick={() => {
                                doPasswordReset();
                              }}
                            >
                              Reset Password
                            </Button>
                          </span>
                        )}
                      </Alert>
                    )}




                    {hasClassBAuth !== "B" && (<Box display={'Flex'} gap={'2'}>
                      <Button rightIcon={<FiEdit3 />} mt={7} width="100%" fontSize="small" bg={colors.buttonBg || "#B92F2F"} color="#fff" variant="solid" borderRadius="30px" onClick={() => setIsEditProfileModalOpen(true)}  >
                        Edit Profile
                      </Button>
                      <Button rightIcon={<RiDeleteBin2Fill />} mt={7} width="100%" fontSize="small" bg="red" color="#fff" variant="solid" borderRadius="30px" onClick={() => setIsDeleteAlertOpen(true)} >
                        Delete Profile
                      </Button></Box>)}
                    <Button rightIcon={<MdLogout />} onClick={handleLogout} mt={2} width="100%" fontSize="small" bg={colors.buttonBg || "#B92F2F"} color="#fff" variant="solid" borderRadius="30px">
                      Log Out
                    </Button>
                  </Box>
                  <AlertDialog
                    isOpen={isDeleteAlertOpen}
                    leastDestructiveRef={cancelRef}
                    onClose={() => setIsDeleteAlertOpen(false)}
                  >
                    <AlertDialogOverlay>
                      <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                          Delete Account
                        </AlertDialogHeader>

                        <AlertDialogBody>
                          <p>
                            Deleting your account will permanently remove all your data, including:
                          </p>
                          <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                            <li>Your profile information</li>
                            <li>Appointment history</li>
                            <li>Saved preferences and settings</li>
                          </ul>
                          <p style={{ marginTop: '10px' }}>
                            <strong>Note:</strong> This action cannot be undone, and you will lose access to your account. Please ensure that you have saved any important information before proceeding.
                          </p>
                          <p style={{ marginTop: '10px', color: 'red' }}>
                            Are you sure you want to delete your account? This action is irreversible.
                          </p>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                          <Button ref={cancelRef} borderRadius="30px" onClick={() => setIsDeleteAlertOpen(false)}>
                            Cancel
                          </Button>
                          <Button colorScheme='red' variant="solid" borderRadius="30px" onClick={handleDeleteAccount} ml={3} isLoading={deleteUserLoading}>
                            Delete
                          </Button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogOverlay>
                  </AlertDialog>
                </TabPanel>
                <TabPanel>
                  <Text fontWeight="bold" fontSize="xl" mt="10px">Change Password</Text>
                  <Text fontSize="sm">Update your password by entering the details below.</Text>
                  <form onSubmit={onChangePassword}>
                    {/* Hidden username field */}
                    <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} />


                    <InputGroup size="md" mt={7}>
                      <Input
                        id="current-password"
                        value={oldPassword}
                        onChange={handleOldPasswordChange}
                        pr="4.5rem"
                        type={show ? "text" : "password"}
                        placeholder="Enter current password"
                        borderRadius="30px"
                        fontSize="sm"
                        autoComplete="current-password"
                      />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                          {show ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>

                    <InputGroup size="md" mt={7}>
                      <Input
                        id="new-password"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        pr="4.5rem"
                        type={show ? "text" : "password"}
                        placeholder="Enter new password"
                        borderRadius="30px"
                        fontSize="sm"
                        autoComplete="new-password"
                      />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                          {show ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>

                    <InputGroup size="md" mt={7}>
                      <Input
                        id="confirm-password"
                        value={confirmNewPassword}
                        onChange={handleConfirmPasswordChange}
                        pr="4.5rem"
                        type={show ? "text" : "password"}
                        placeholder="Confirm new password"
                        borderRadius="30px"
                        fontSize="sm"
                        autoComplete="new-password"
                      />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                          {show ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>

                    <Box mt={4}>
                      <Text fontSize="sm" mb={2}>New password must contain:</Text>
                      <SimpleGrid columns={1} spacing={2}>
                        <Text display="flex" alignItems="center">
                          <Box as={passwordCriteria.minLength ? FiCheck : FiX} color={passwordCriteria.minLength ? "green.500" : "red.500"} />
                          <Text fontSize="sm" ml={2}>At least 8 characters</Text>
                        </Text>
                        <Text display="flex" alignItems="center">
                          <Box as={passwordCriteria.uppercase ? FiCheck : FiX} color={passwordCriteria.uppercase ? "green.500" : "red.500"} />
                          <Text fontSize="sm" ml={2}>An uppercase letter</Text>
                        </Text>
                        <Text display="flex" alignItems="center">
                          <Box as={passwordCriteria.lowercase ? FiCheck : FiX} color={passwordCriteria.lowercase ? "green.500" : "red.500"} />
                          <Text fontSize="sm" ml={2}>A lowercase letter</Text>
                        </Text>
                        <Text display="flex" alignItems="center">
                          <Box as={passwordCriteria.number ? FiCheck : FiX} color={passwordCriteria.number ? "green.500" : "red.500"} />
                          <Text fontSize="sm" ml={2}>A number</Text>
                        </Text>
                        <Text display="flex" alignItems="center">
                          <Box as={passwordCriteria.specialChar ? FiCheck : FiX} color={passwordCriteria.specialChar ? "green.500" : "red.500"} />
                          <Text fontSize="sm" ml={2}>A special character</Text>
                        </Text>
                      </SimpleGrid>
                    </Box>

                    {newPassword !== confirmNewPassword && confirmNewPassword && (
                      <Text mt={4} color="red.500" fontSize="sm" fontWeight="bold">
                        New password and Confirm password do not match.
                      </Text>
                    )}


                    <Button mt={7} width="100%" fontSize="small" bg={colors.buttonBg || "#B92F2F"} color="#fff" variant="solid" borderRadius="30px" type="submit">
                      Update Password
                    </Button>
                  </form>


                </TabPanel>
                <TabPanel>
                  <Text fontWeight="bold" fontSize="xl" mt="10px">
                    My Family
                  </Text>
                  <Text fontSize="sm" mb={5}>
                    Add new members and manage existing family account.
                  </Text>

                  {renderFamilyMembers()}
                  <Button mt={7} width="100%" fontSize="small" bg={colors.buttonBg || "#B92F2F"} color="#fff" variant="solid" borderRadius="30px" onClick={onOpenCreateFamilyMember}>
                    Add new Family member
                  </Button>
                </TabPanel>
                <TabPanel>
                  <Text fontWeight="bold" fontSize="xl" mt="10px">
                    My Bookings
                  </Text>
                  <Text fontSize="sm" mb={5}>
                    Here you can find all the bookings you made
                  </Text>
                  {renderAppointments()}
                  {/* <Button mt={7} width="100%" fontSize="small" bg="#B92F2F" color="#fff" variant="solid" borderRadius="30px">
                  Save
                </Button> */}
                </TabPanel>
                <TabPanel>
                  <Button
                    mt={4}
                    mb={4}
                    width="100%"
                    fontSize="small"
                    bg={colors.buttonBg || "#B92F2F"}
                    color="#fff"
                    variant="solid"
                    borderRadius="30px"
                    as="a"
                    href="/manual.pdf"
                    download="manual.pdf"
                  >
                    Download User Manual
                  </Button>

                  <Accordion allowToggle mt={5}>
                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Introduction
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        This manual provides comprehensive instructions on using various functionalities of the web booking application. Each section includes step-by-step guides to ensure users can efficiently navigate the application, book appointments, and manage their accounts.
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./appadd.jpg"
                          onClick={() => handleImageClick("./T4.jpg")}
                        ></Image> */}

                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Filter Doc Functionality
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>

                      <AccordionPanel pb={4}>
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./T1.jpg"
                          onClick={() => handleImageClick("./T1.jpg")}
                        ></Image> */}

                        <Text fontWeight="bold">Login to the Application:</Text>
                        <Text>
                          1. Click on the "Login" button at the screen's top right corner.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Patient Status:</Text>
                        <Text>
                          1. Use the toggle to answer "Are you an existing patient?"<br />
                          - The default selection is No (New Patient).<br />
                          - If you are an existing patient, switch the toggle to "Yes."
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Appointment For:</Text>
                        <Text>
                          1. Use the toggle to answer "Is this appointment for you?"<br />
                          - The default selection is Yes (For me).<br />
                          - If the appointment is for a family member, switch to "No."<br />
                          - A modal will appear listing existing family members.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Family Member:</Text>
                        <Text>
                          1. From the modal, select the family member who needs the appointment.<br />
                          2. If the family member is not listed, click on "Add new Family Member."
                        </Text>

                        <Text fontWeight="bold" mt={4}>Add New Family Member:</Text>
                        <Text>
                          1. Fill out the form with the following details:<br />
                          - First Name<br />
                          - Last Name<br />
                          - Date of Birth<br />
                          - Gender<br />
                          - Mobile Number<br />
                          2. Click on "Create an Account" to save the new family member.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Appointment Type:</Text>
                        <Text>
                          1. Click on "Select Appointment Type."<br />
                          - The default selection is Standard Appointment - New patient.<br />
                          - To change the appointment type, first select the service type.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Service Type:</Text>
                        <Text>
                          1. Choose from the available options from the list provided (e.g., General Practitioner, Telehealth, COVID Vaccine, etc.).
                        </Text>

                        <Text fontWeight="bold" mt={4}>Select Specific Appointment Type:</Text>
                        <Text>
                          1. Based on the selected service type, choose the specific appointment type from the list provided (e.g., Standard Appointment, Cervical Screening, etc.).
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>




                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Doctor Card Functionality
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./T2.jpg"
                          onClick={() => handleImageClick("./T2.jpg")}
                        ></Image> */}

                        <Text fontWeight="bold">The "Doctor card" functionality allows users to:</Text>
                        <Text>- View detailed information about doctors</Text>
                        <Text>- Check their available appointment times</Text>
                        <Text>- Book appointments</Text>

                        <Text fontWeight="bold" mt={4}>Viewing Doctor’s Details:</Text>
                        <Text>
                          1. Navigate to the doctor’s section in the application.<br />
                          2. Locate the doctor card of the desired doctor (e.g., Dr. Esther Bischoff).<br />
                          3. Click on the "More Details" button to view detailed information about the doctor.<br />
                          4. Read the detailed description in the modal and click "Done" to close it.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Checking First Available Time:</Text>
                        <Text>
                          1. Check the first available appointment time displayed on the doctor’s card.<br />
                          2. If the first available time suits you, proceed to book the appointment.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Selecting More Available Times:</Text>
                        <Text>
                          1. If you need a different time slot, click on the "More Appointment Times" button.<br />
                          2. Browse the available time slots in the schedule.<br />
                          3. Use the "Next" button to view more slots in the upcoming weeks if needed.<br />
                          4. Select a suitable time slot for your appointment.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Confirming Appointment:</Text>
                        <Text>
                          1. After selecting a time slot, review the personal and appointment details.<br />
                          2. Ensure your first name and date of birth are correct.<br />
                          3. Verify the appointment details such as date, time, duration, doctor’s name, and clinic name.<br />
                          4. Click on the "Confirm Appointment" button to finalize the booking.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Booking Successful:</Text>
                        <Text>
                          1. Once the appointment is confirmed, a success message will be displayed.<br />
                          2. You can add the appointment to your preferred calendar by selecting one of the options (iCal, Calendar, MS Teams).<br />
                          3. Click on the "Return to Home" button to go back to the home screen.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Login Process
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./T7.jpg"
                          onClick={() => handleImageClick("./T7.jpg")}
                        ></Image> */}

                        <Text fontWeight="bold">Opening the Login Modal:</Text>
                        <Text>Click on the "Login" button to open the "Login" modal. The modal provides options to log in or create a new account.</Text>

                        <Text fontWeight="bold" mt={4}>Logging In:</Text>
                        <Text>
                          1. Input your email and password in the provided fields.<br />
                          2. Use the eye icon to toggle password visibility if needed.<br />
                          3. If you have forgotten your password, click on "Forgot password" to recover it.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Multi-Factor Authentication (MFA):</Text>
                        <Text>
                          1. After entering your email and password, you will need to input the MFA code sent to your mobile via SMS.<br />
                          2. Enter the MFA code in the provided field.<br />
                          3. Click the "Confirm" button to complete the login process.
                        </Text>

                        <Text fontWeight="bold" mt={4}>Completing Login:</Text>
                        <Text>Click the "Login" button after entering your credentials and the MFA code to access your account.</Text>

                        <Text fontWeight="bold" mt={4}>Creating a New User Account:</Text>
                        <Text>Click the "Create an Account" button to begin the account creation process.</Text>

                        <Text fontWeight="bold" mt={4}>Entering Personal Details:</Text>
                        <Text>Fill in your first name, last name, date of birth, gender, email, and mobile number. Click the "Save User" button to continue.</Text>

                        <Text fontWeight="bold" mt={4}>Setting a Password:</Text>
                        <Text>Enter a password that meets the security requirements (at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character). Confirm the password by entering it again. Click the "Save Password" button to create your account.</Text>

                        <Text fontWeight="bold" mt={4}>Account Creation Confirmation:</Text>
                        <Text>A "Successful!" message will appear once the account is created. Click "Return to Home" to go back to the main screen.</Text>

                        <Text fontWeight="bold" mt={4}>Making an Appointment Without Login</Text>
                        <Text>If you need to make an appointment without logging in, click "Continue with details".<br />
                          Entering Appointment Details:<br />
                          Enter your first name, second name, email, phone number, and date of birth.<br />
                          Click "Next" to proceed.</Text>

                        <Text fontWeight="bold" mt={4}>Clinic Registration (If Required)</Text>
                        <Text>Clinic Registration Prompt:<br />
                          If you are not registered with the specific clinic, you will be prompted to register.<br />
                          Entering Required Data for Clinic Registration:<br />
                          Provide details such as birth sex, Medicare number, Medicare valid date, Concession card number, and Concession card valid date.<br />
                          Click "Next" to continue.<br />
                          Providing Address Details:<br />
                          Enter your address, suburb, and postal code.<br />
                          Click "Next" to complete the clinic registration.<br />
                          Registration Success Confirmation:<br />
                          A confirmation message will indicate that you have successfully registered with the clinic.<br />
                          Click "Continue" to proceed with booking or other actions.</Text>


                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Repeat Script Process
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./T4.jpg"
                          onClick={() => handleImageClick("./T4.jpg")}
                        ></Image> */}

                        <Text fontWeight="bold">Access the Repeat Script Function:</Text>
                        <Text>When the user needs to request a repeat prescription for medications, they can select the Repeat Scripts option.</Text>

                        <Text fontWeight="bold" mt={4}>Select Doctor:</Text>
                        <Text>The user will be presented with a list of doctors. They must select the doctor from whom they want to request the repeat script by clicking the relevant doctor’s name.</Text>

                        <Text fontWeight="bold" mt={4}>List Required Medications:</Text>
                        <Text>After selecting the doctor, the user needs to list the medications they wish to repeat. The user can add up to 5 medications, including details such as dosage and frequency. After entering the required information, click Add Medication.</Text>

                        <Text fontWeight="bold" mt={4}>Choose Delivery Method:</Text>
                        <Text>The user is prompted to select their preferred method of delivery for receiving the prescription. Available options include collection from the clinic, fax, eScript, or postal delivery. The costs associated with each method are displayed.</Text>

                        <Text fontWeight="bold" mt={4}>Review and Confirm Payment:</Text>
                        <Text>Before completing the request, the user reviews all the entered details, including patient information, medication details, doctor details, and the chosen delivery method. The user clicks Proceed to Payment once all the details are confirmed.</Text>

                        <Text fontWeight="bold" mt={4}>Enter Payment Information:</Text>
                        <Text>The user enters their card details, agrees to the terms, and authorizes the payment by clicking Authorize.</Text>

                        <Text fontWeight="bold" mt={4}>Success Confirmation:</Text>
                        <Text>Once the payment is successfully processed, a confirmation screen will appear showing that the payment was successful.</Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Repeat Referral Process
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {/* <Image

                          sx={{

                            width: '100%'
                          }}
                          src="./T5.jpg"
                          onClick={() => handleImageClick("./T5.jpg")}
                        ></Image> */}

                        <Text fontWeight="bold">Access the Repeat Referral Function:</Text>
                        <Text>When the user needs to request a repeat referral, they can select the Repeat Referral option.</Text>

                        <Text fontWeight="bold" mt={4}>Select Doctor:</Text>
                        <Text>The user will be shown a list of doctors from which they can request a repeat referral. They should select the relevant doctor by clicking their name.</Text>

                        <Text fontWeight="bold" mt={4}>Enter Specialist Details:</Text>
                        <Text>The user must enter details about the specialist, including the specialist’s name, address, reason for referral, and any special notes.</Text>

                        <Text fontWeight="bold" mt={4}>Choose Delivery Method:</Text>
                        <Text>The user is then prompted to select their preferred delivery method for the referral. Options such as collection from the clinic or fax are available, with the associated costs displayed.</Text>

                        <Text fontWeight="bold" mt={4}>Review and Confirm Payment:</Text>
                        <Text>Before completing the request, the user reviews all the entered details, including patient information, specialist details, doctor details, and the chosen delivery method. If everything is correct, the user clicks Proceed to Payment.</Text>

                        <Text fontWeight="bold" mt={4}>Enter Payment Information:</Text>
                        <Text>The user enters their card details, agrees to the terms, and authorizes the payment by clicking Authorize.</Text>

                        <Text fontWeight="bold" mt={4}>Success Confirmation:</Text>
                        <Text>Once the payment is successfully processed, a confirmation screen will show that the payment was successful, and the referral request has been lodged.</Text>
                      </AccordionPanel>
                    </AccordionItem>




                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box as="span" flex="1" textAlign="left">
                            Frequently Asked Questions (FAQs)
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>

                      <AccordionPanel pb={4}>
                        <Text fontWeight="bold" mt={4}>1. How do I create a new account?</Text>
                        <Text>
                          To create a new account:<br />
                          1. Click on the "Login" button at the top right corner of the screen.<br />
                          2. In the login modal, click on "Create an Account."<br />
                          3. Fill in your details such as first name, last name, date of birth, gender, email, and mobile number.<br />
                          4. Click the "Save User" button.<br />
                          5. Set a password that meets the security requirements and confirm it.<br />
                          6. Click the "Save Password" button.<br />
                          7. A success message will appear once your account is created. Click "Return to Home" to go back to the main screen.
                        </Text>

                        <Text fontWeight="bold" mt={4}>2. How do I log into my account?</Text>
                        <Text>
                          To log into your account:<br />
                          1. Click on the "Login" button at the top right corner of the screen.<br />
                          2. Enter your email and password in the provided fields.<br />
                          3. If prompted, enter the Multi-Factor Authentication (MFA) code sent to your mobile via SMS.<br />
                          4. Click the "Confirm" button to complete the login process.
                        </Text>

                        <Text fontWeight="bold" mt={4}>3. What should I do if I forget my password?</Text>
                        <Text>
                          If you forget your password:<br />
                          1. Click on the "Login" button at the top right corner of the screen.<br />
                          2. In the login modal, click on "Forgot password."<br />
                          3. Follow the instructions to reset your password. An email with a reset link will be sent to your registered email address.
                        </Text>

                        <Text fontWeight="bold" mt={4}>4. How do I book an appointment for a family member?</Text>
                        <Text>
                          To book an appointment for a family member:<br />
                          1. Log into your account.<br />
                          2. Use the toggle to answer "Is this appointment for you?" and switch it to "No."<br />
                          3. Select the family member from the modal. If the family member is not listed, click on "Add new Family Member" and fill in their details.<br />
                          4. Continue with the appointment booking process by selecting the appointment type and confirming the details.
                        </Text>

                        <Text fontWeight="bold" mt={4}>5. How do I change my password?</Text>
                        <Text>
                          To change your password:<br />
                          1. Open the "My Account" drawer by clicking on the "Hi [Username]" button.<br />
                          2. Go to the "Change My Password" tab.<br />
                          3. Enter your current password.<br />
                          4. Enter your new password and confirm it by entering it again.<br />
                          5. Click the "Update Password" button to change your password.
                        </Text>

                        <Text fontWeight="bold" mt={4}>6. How do I view and manage my profile information?</Text>
                        <Text>
                          To view and manage your profile information:<br />
                          1. Open the "My Account" drawer by clicking on the "Hi [Username]" button.<br />
                          2. Go to the "My Profile" tab.<br />
                          3. You can view your details such as name, date of birth, gender, email address, and mobile number.<br />
                          4. To edit your profile, click on the "Edit Profile" button, make the necessary changes, and click "Save."
                        </Text>

                        <Text fontWeight="bold" mt={4}>7. How do I add a new family member to my account?</Text>
                        <Text>
                          To add a new family member:<br />
                          1. Open the "My Account" drawer by clicking on the "Hi [Username]" button.<br />
                          2. Go to the "My Family" tab.<br />
                          3. Click on the "Add new Family Member" button.<br />
                          4. Fill in the new family member's details and click "Save."
                        </Text>

                        <Text fontWeight="bold" mt={4}>8. How do I view my upcoming appointments?</Text>
                        <Text>
                          To view your upcoming appointments:<br />
                          1. Open the "My Account" drawer by clicking on the "Hi [Username]" button.<br />
                          2. Go to the "My Bookings" tab.<br />
                          3. You will see a list of your upcoming appointments with details such as the doctor's name, appointment type, date, and time.
                        </Text>

                        <Text fontWeight="bold" mt={4}>9. What are the security requirements for my password?</Text>
                        <Text>Your password must contain at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.</Text>

                        <Text fontWeight="bold" mt={4}>10. How do I log out of my account?</Text>
                        <Text>
                          To log out of your account:<br />
                          1. Open the "My Account" drawer by clicking on the "Hi [Username]" button.<br />
                          2. Click the "Log Out" button available in each tab.
                        </Text>

                        <Text fontWeight="bold" mt={4}>11. How do I book a specific type of appointment?</Text>
                        <Text>
                          To book a specific type of appointment:<br />
                          1. Log into your account.<br />
                          2. Select the service type (e.g., General Practitioner, Telehealth, COVID Vaccine, Acupuncture).<br />
                          3. Choose the specific appointment type from the list provided (e.g., Standard Appointment, Cervical Screening).<br />
                          4. Follow the prompts to select a time slot and confirm your appointment.
                        </Text>

                        <Text fontWeight="bold" mt={4}>12. How do I contact customer support?</Text>
                        <Text>
                          If you need assistance, you can contact customer support by:<br />
                          1. Sending an email to the support team at [feedback@familydoctor.com.au].<br />
                          2. Calling the support hotline provided in the application’s contact section.<br />
                          
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>
                <TabPanel>

                  <iframe
                    src="https://familydoctor.com.au/privacy-policy-family-doctor-2/"
                    title="Privacy Policy"
                    width="100%"
                    height="1000px"
                    style={{ border: 'none' }}
                  ></iframe>
                </TabPanel>


              </TabPanels>)}
          </Tabs>

        </DrawerBody>
      </DrawerContent>
      <EditProfileModal isOpen={isEditProfileModalOpen} onClose={() => setIsEditProfileModalOpen(false)} userData1={userData} />
      {selectedImage && (
        <Modal isOpen={isImageOpen} onClose={handleImageClose} size="full">
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody display="flex" justifyContent="center" alignItems="center">
              <Image src={selectedImage} maxW="100vw" />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Drawer>
  );
};

export default UserDetails;
